/**
 * @fileoverview Share Texts Hook
 * 
 * Fetches configurable share message templates from the database
 * and provides a function to interpolate placeholders.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ShareText {
  id: string;
  label: string;
  template: string;
  description: string | null;
  updated_at: string;
}

export function useShareTexts() {
  const { data: shareTexts } = useQuery({
    queryKey: ['share-texts'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('share_texts')
        .select('*');
      if (error) throw error;
      return data as ShareText[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000,
  });

  const getShareText = (
    type: string | undefined,
    vars: Record<string, string | undefined> = {}
  ): string | null => {
    if (!shareTexts) return null;

    const row = shareTexts.find((r) => r.id === type)
      || shareTexts.find((r) => r.id === 'default');

    if (!row) return null;

    return row.template.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? '');
  };

  return { shareTexts, getShareText };
}
